import { Injectable } from '@angular/core'
import { BehaviorSubject, concat, forkJoin, from, merge, Observable, of, timer } from 'rxjs'
import { delay, finalize, map, switchMap, take, takeUntil, tap } from 'rxjs/operators'
import { CrudAction, CrudEvent, CrudSubject } from 'src/utils/crud-subject'
import { Arn, MatchingRuleInfo, SelectableResource, Tag } from '../../../utils/types'
import { ResourceType } from 'src/utils/resource-types'
import { ApiService } from '../../services/api.service'
import {
  adaptApplicationIssueDto,
  AllRuleDto,
  ApplicationDto,
  ApplicationIntegrationDto,
  ApplicationIssueDto,
  ApplicationIssueScopeDto,
  ApplicationResourceDto,
  ApplicationResourcesResponseDto,
  ApplicationRestoreRequestDto,
  ApplicationType,
  AppResourceStatusChangeDto,
  AppResourceStatusChangesResponseDto,
  ArnRuleDto,
  AwsTargetResourceDto,
  BaseRuleDto,
  ExcludeWeakArnRuleDto,
  RecoveryPointAttemptDto,
  RecoveryPointDto,
  ResourceConfigDto,
  ResourceGroupRuleDto,
  ResourceTypeRuleDto,
  RestoreTriggerDto,
  SyncPhaseDto,
  TagRuleDto,
  RestoreRequestDto,
} from '../../services/dtos'
import { SyncPair, SyncPairProviderService } from './sync-pair.service'
import { AccountService } from './account.service'
import { SyncSource, SyncSourceProviderService } from './sync-source.service'
import { ApplicationResource } from 'src/app/shared/resource-viewer-base'
import { RelevantStatus } from 'src/app/shared/types'
import { areDeeplyEqual, dateDiff, diffSets, isLaterThan, mapToObject, maxDate } from 'src/app/shared/utils'
import { adaptApplicationIssueDtoToRaisedIssue, DeleteFailedIssue, RaisedIssue, RaisedIssueScope } from './issue'
import { ResourceStatus } from 'src/app/account/components/app-resources-view/app-resources-view.component'
import { computeProcessStatus, ProcessStatus } from 'src/app/account/components/process-state/process-state.component'

export interface BaseRule {}

export class ArnRule implements BaseRule {
  static ruleType = 'arn'
  arnSet: Array<Arn>
  operator: string

  constructor(dto: ArnRuleDto) {
    this.arnSet = dto.arns
    this.operator = dto.operator || 'is'
  }
}

export class ExcludeWeakArnRule implements BaseRule {
  static ruleType = 'excludeWeakArn'
  arnSet: Array<Arn>
  operator: string

  constructor(dto: ExcludeWeakArnRuleDto) {
    this.arnSet = dto.arns
    this.operator = dto.operator || 'is'
  }
}

export class TagRule implements BaseRule {
  static ruleType = 'tag'
  tagName: string
  tagValue: string | null
  operator: string

  constructor(dto: TagRuleDto) {
    this.tagName = dto.name
    this.tagValue = dto.value
    this.operator = dto.operator || 'is'
  }
}

export class ResourceTypeRule implements BaseRule {
  static ruleType = 'resourceType'
  resourceType: string
  operator: string

  constructor(dto: ResourceTypeRuleDto) {
    this.resourceType = dto.resourceType
    this.operator = dto.operator || 'is'
  }
}

export class ResourceGroupRule implements BaseRule {
  static ruleType = 'resourceGroup'
  resourceGroup: string
  operator: string

  constructor(dto: ResourceGroupRuleDto) {
    this.resourceGroup = dto.resourceGroup
    this.operator = dto.operator || 'is'
  }
}

export class AllRule implements BaseRule {
  static ruleType = 'all'
  rules: BaseRule[]

  constructor(dto: AllRuleDto) {
    this.rules = dto.rules.map(ruleDtoToRule)
  }
}

function ruleDtoToRule(dto: BaseRuleDto) {
  switch (dto.ruleType) {
    case ArnRule.ruleType:
      return new ArnRule(dto as ArnRuleDto)
    case ExcludeWeakArnRule.ruleType:
      return new ExcludeWeakArnRule(dto as ExcludeWeakArnRuleDto)
    case AllRule.ruleType:
      return new AllRule(dto as AllRuleDto)
    case TagRule.ruleType:
      return new TagRule(dto as TagRuleDto)
    case ResourceTypeRule.ruleType:
      return new ResourceTypeRule(dto as ResourceTypeRuleDto)
    case ResourceGroupRule.ruleType:
      return new ResourceGroupRule(dto as ResourceGroupRuleDto)
  }
  throw `Unknown rule type: ${dto.ruleType}`
}

function createTagRuleDto(tag: Tag, action?: string): TagRuleDto {
  const dto: TagRuleDto = {
    ruleType: TagRule.ruleType,
    name: tag.tagName,
    value: tag.tagValue ?? null,
    operator: (tag as any).operator ?? 'is',
    action: action === 'exclude' ? 'exclude' : null,
  }
  return dto
}

function createResourceTypeRuleDto(resourceTypeRule: ResourceTypeRule, action?: string): ResourceTypeRuleDto {
  const dto: ResourceTypeRuleDto = {
    ruleType: ResourceTypeRule.ruleType,
    resourceType: resourceTypeRule.resourceType,
    operator: resourceTypeRule.operator || 'is',
    action: action === 'exclude' ? 'exclude' : null,
  }
  return dto
}

function createResourceGroupRuleDto(resourceGroupRule: ResourceGroupRule, action?: string): ResourceGroupRuleDto {
  const dto: ResourceGroupRuleDto = {
    ruleType: ResourceGroupRule.ruleType,
    resourceGroup: resourceGroupRule.resourceGroup,
    operator: resourceGroupRule.operator || 'is',
    action: action === 'exclude' ? 'exclude' : null,
  }
  return dto
}

function createAllRuleDto(allRule: AllRule, action?: string): AllRuleDto {
  const dto: AllRuleDto = {
    ruleType: AllRule.ruleType,
    rules: allRule.rules.map((rule) => {
      if (rule instanceof TagRule) {
        return createTagRuleDto(rule, action)
      } else if (rule instanceof ArnRule) {
        return createArnRuleDto(new Set(rule.arnSet))
      } else if (rule instanceof ExcludeWeakArnRule) {
        return createExcludeWeakArnRuleDto(new Set(rule.arnSet))
      } else if (rule instanceof ResourceTypeRule) {
        return createResourceTypeRuleDto(rule, action)
      } else if (rule instanceof ResourceGroupRule) {
        return createResourceGroupRuleDto(rule, action)
      } else {
        throw new Error(`Unknown rule type: ${rule.constructor.name}`)
      }
    }),
    action: action === 'exclude' ? 'exclude' : null,
  }

  return dto
}

function createArnRuleDto(arns: Set<Arn>): ArnRuleDto {
  return {
    ruleType: ArnRule.ruleType,
    arns: Array.from(arns),
    operator: 'is',
  }
}

function createExcludeWeakArnRuleDto(arns: Set<Arn>): ExcludeWeakArnRuleDto {
  return {
    ruleType: ExcludeWeakArnRule.ruleType,
    arns: Array.from(arns),
    operator: 'is',
  }
}

export interface RestoreRequest {
  application: Application
  recoveryPointId?: string
  isRealTime?: boolean
  networkSandboxEnabled?: boolean
  advancedNetworkSandbox?: boolean
  networkSandboxWhitelist?: string[]
  networkSandboxAllowedDomains?: string[]
  networkSandboxAllowedServices?: string[]
  quarantineEnabled?: boolean
  malwareScanEnabled?: boolean
  forceNewTargetPlan?: boolean
  protectRecoveryPoint?: boolean
}

function adaptRestoreRequest(req: RestoreRequest, syncPhase: SyncPhaseDto): [string, ApplicationRestoreRequestDto] {
  let dto = {
    phase: syncPhase,
  } as ApplicationRestoreRequestDto

  if (!!req.recoveryPointId) {
    dto.recoveryPointId = req.recoveryPointId
  }
  if (req.isRealTime !== undefined) {
    dto.enableRealTimeSync = req.isRealTime
  }
  if (req.networkSandboxEnabled !== undefined) {
    dto.enableNetworkSandbox = req.networkSandboxEnabled
  }
  if (dto.enableNetworkSandbox && req.networkSandboxWhitelist !== undefined) {
    dto.networkSandboxWhitelist = req.networkSandboxWhitelist
  }
  if (dto.enableNetworkSandbox && req.networkSandboxAllowedDomains !== undefined) {
    dto.networkSandboxAllowedDomains = req.networkSandboxAllowedDomains
  }
  if (dto.enableNetworkSandbox && req.networkSandboxAllowedServices !== undefined) {
    dto.networkSandboxAllowedServices = req.networkSandboxAllowedServices
  }
  if (req.advancedNetworkSandbox !== undefined) {
    dto.advancedNetworkSandbox = req.advancedNetworkSandbox
  }
  if (req.quarantineEnabled !== undefined) {
    dto.enableQuarantine = req.quarantineEnabled
  }
  if (req.malwareScanEnabled !== undefined) {
    dto.enableMalwareScan = req.malwareScanEnabled
  }
  if (req.forceNewTargetPlan !== undefined) {
    dto.forceNewTargetPlan = req.forceNewTargetPlan
  }
  if (req.protectRecoveryPoint) {
    // can only be set to true during restore
    dto.protectRecoveryPoint = req.protectRecoveryPoint
  }
  return [req.application.applicationId, dto]
}

export class Application {
  static BACKUP_POLL_INTERVAL = 15000
  static RESTORE_POLL_INTERVAL = 2000
  static BACKGROUND_POLL_INTERVAL = 30000

  private static _matchesRuleSet(
    resource: SelectableResource,
    rules: Array<Tag | AllRule | ResourceTypeRule | ResourceGroupRule>,
    setMatchingRules: boolean = false
  ): Map<string, MatchingRuleInfo> {
    /**
     * Helper function to check if a resource matches any rule in a set of rules.
     * Top-level rules use OR logic (any rule can match).
     * AllRule inner rules use AND logic (all inner rules must match).
     * Returns a Map of matching rules (empty if no matches).
     */
    const matchingRulesMap = new Map<string, MatchingRuleInfo>()

    // OR logic: iterate through each top-level rule
    for (const topLevelRule of rules) {
      if ('rules' in topLevelRule) {
        // Handle AllRule (AND logic for inner rules)
        let allRule = topLevelRule as AllRule

        // Separate tag rules, resource type rules, and resource group rules
        const tagRules = allRule.rules.filter((rule) => rule instanceof TagRule) as TagRule[]
        const resourceTypeRules = allRule.rules.filter((rule) => rule instanceof ResourceTypeRule) as ResourceTypeRule[]
        const resourceGroupRules = allRule.rules.filter(
          (rule) => rule instanceof ResourceGroupRule
        ) as ResourceGroupRule[]

        // For AllRule: ALL inner rules must match (AND logic)
        const matchingTagRules = new Map<string, MatchingRuleInfo>()
        const allTagRulesMatch =
          tagRules.length === 0 ||
          tagRules.every((rule) => {
            const tagMatches =
              (rule.tagValue === null && resource.tags.has(rule.tagName)) ||
              resource.tags.get(rule.tagName) === rule.tagValue
            const ruleResult = rule.operator === 'is_not' ? !tagMatches : tagMatches
            if (ruleResult) {
              const matchingRuleInfo = { value: rule.tagValue, operator: rule.operator || 'is' }
              matchingTagRules.set(rule.tagName, matchingRuleInfo)
            }
            return ruleResult
          })

        const matchingResourceTypeRules = new Map<string, MatchingRuleInfo>()
        const allResourceTypeRulesMatch =
          resourceTypeRules.length === 0 ||
          resourceTypeRules.every((rule) => {
            const resourceTypeMatches = resource.resourceType === rule.resourceType
            const ruleResult = rule.operator === 'is_not' ? !resourceTypeMatches : resourceTypeMatches
            if (ruleResult) {
              const key = `__resourceType:${rule.resourceType}`
              const matchingRuleInfo = { value: rule.resourceType, operator: rule.operator || 'is' }
              matchingResourceTypeRules.set(key, matchingRuleInfo)
            }
            return ruleResult
          })

        const matchingResourceGroupRules = new Map<string, MatchingRuleInfo>()
        const allResourceGroupRulesMatch =
          resourceGroupRules.length === 0 ||
          resourceGroupRules.every((rule) => {
            // For Azure Resource Group resources, use the resource name directly
            let resourceGroupValue = resource.resourceGroup
            if (resource.resourceType === ResourceType.azureResourceGroup) {
              resourceGroupValue = resource.resourceName
            }

            // Case-insensitive comparison for Azure resource groups
            const resourceGroupMatches = resourceGroupValue?.toLowerCase() === rule.resourceGroup?.toLowerCase()
            const ruleResult = rule.operator === 'is_not' ? !resourceGroupMatches : resourceGroupMatches
            if (ruleResult) {
              const key = `__resourceGroup:${rule.resourceGroup}`
              const matchingRuleInfo = { value: rule.resourceGroup, operator: rule.operator || 'is' }
              matchingResourceGroupRules.set(key, matchingRuleInfo)
            }
            return ruleResult
          })

        // The AllRule matches if ALL tag rules match AND ALL resource type rules match AND ALL resource group rules match
        if (allTagRulesMatch && allResourceTypeRulesMatch && allResourceGroupRulesMatch) {
          // Add all matching rules from this AllRule to the result
          if (setMatchingRules) {
            matchingTagRules.forEach((value, key) => {
              resource.matchingRules.set(key, value)
            })
            matchingResourceTypeRules.forEach((value, key) => {
              resource.matchingRules.set(key, value)
            })
            matchingResourceGroupRules.forEach((value, key) => {
              resource.matchingRules.set(key, value)
            })
          }

          matchingTagRules.forEach((value, key) => {
            matchingRulesMap.set(key, value)
          })
          matchingResourceTypeRules.forEach((value, key) => {
            matchingRulesMap.set(key, value)
          })
          matchingResourceGroupRules.forEach((value, key) => {
            matchingRulesMap.set(key, value)
          })

          // Since this is OR logic at the top level, if any rule matches, we can continue to check other rules
          // but we don't return early since we want to collect all matching rules
        }
      } else if (topLevelRule instanceof ResourceGroupRule) {
        // Handle top-level ResourceGroupRule
        // For Azure Resource Group resources, use the resource name directly
        let resourceGroupValue = resource.resourceGroup
        if (resource.resourceType === ResourceType.azureResourceGroup) {
          resourceGroupValue = resource.resourceName
        }

        // Case-insensitive comparison for Azure resource groups
        const resourceGroupMatches = resourceGroupValue?.toLowerCase() === topLevelRule.resourceGroup?.toLowerCase()
        const matchResult = topLevelRule.operator === 'is_not' ? !resourceGroupMatches : resourceGroupMatches
        if (matchResult) {
          const key = `__resourceGroup:${topLevelRule.resourceGroup}`
          const matchingRuleInfo = { value: topLevelRule.resourceGroup, operator: topLevelRule.operator || 'is' }
          if (setMatchingRules) {
            resource.matchingRules.set(key, matchingRuleInfo)
          }
          matchingRulesMap.set(key, matchingRuleInfo)
        }
      } else if (topLevelRule instanceof ResourceTypeRule) {
        // Handle top-level ResourceTypeRule
        const resourceTypeMatches = resource.resourceType === topLevelRule.resourceType
        const matchResult = topLevelRule.operator === 'is_not' ? !resourceTypeMatches : resourceTypeMatches
        if (matchResult) {
          const key = `__resourceType:${topLevelRule.resourceType}`
          const matchingRuleInfo = { value: topLevelRule.resourceType, operator: topLevelRule.operator || 'is' }
          if (setMatchingRules) {
            resource.matchingRules.set(key, matchingRuleInfo)
          }
          matchingRulesMap.set(key, matchingRuleInfo)
        }
      } else {
        // Handle top-level Tag
        let { tagName, tagValue, operator } = topLevelRule as Tag
        const tagMatches = (tagValue === null && resource.tags.has(tagName)) || resource.tags.get(tagName) === tagValue
        const ruleResult = operator === 'is_not' ? !tagMatches : tagMatches
        if (ruleResult) {
          const matchingRuleInfo = { value: tagValue, operator: operator || 'is' }
          if (setMatchingRules) {
            resource.matchingRules.set(tagName, matchingRuleInfo)
          }
          matchingRulesMap.set(tagName, matchingRuleInfo)
        }
      }
    }

    return matchingRulesMap
  }

  private static _updateResourceRuleSelection(
    matchingArns: Set<Arn>,
    suppressedArns: Set<Arn>,
    matchingRules: Array<Tag | AllRule | ResourceTypeRule | ResourceGroupRule>,
    excludingRules: Array<Tag | AllRule | ResourceTypeRule | ResourceGroupRule>,
    applicationResources: Array<SelectableResource>
  ) {
    /**
     * Does an in-place update of the ApplicationResource fields related to selection rules.
     */

    let transitiveMatchesToProcess: Array<string> = new Array<string>()
    let excludedResourceArns = new Set<string>()

    // First, identify all excluded resources
    applicationResources.forEach((resource) => {
      if (resource.resourceArn && resource.tags) {
        const excludeRuleResults = Application._matchesRuleSet(resource, excludingRules, false)
        if (excludeRuleResults.size > 0) {
          excludedResourceArns.add(resource.resourceArn)
        }
      }
    })

    applicationResources.forEach((resource) => {
      let matchesRule = false

      if (resource.resourceArn && resource.tags) {
        // Look to see if this ID matches one that should be selected.
        if (matchingArns.has(resource.resourceArn)) {
          resource.arnMatch = true
          matchesRule = true
        }

        // Also check on tag matches using the extracted helper function
        const matchingRuleResults = Application._matchesRuleSet(resource, matchingRules, true)
        if (matchingRuleResults.size > 0) {
          matchesRule = true
        }

        if (matchesRule) {
          transitiveMatchesToProcess.push(resource.resourceArn)
        }
      }
    })

    // Now that we've marked the resources that match rules explicitly, discover the
    // transitive matches and mark them as well.
    let processedMatches = new Set<string>()
    let kvMapArray = applicationResources.map((res) => [res.resourceArn, res] as [string, SelectableResource])
    let appResourceMap = new Map(kvMapArray)

    const parentChildMap = new Map<string, SelectableResource[]>()
    applicationResources.forEach((resource) => {
      if (resource.parentArn) {
        let children = parentChildMap.get(resource.parentArn)
        if (!children) {
          children = []
          parentChildMap.set(resource.parentArn, children)
        }
        children.push(resource)
      }
    })

    function _getDescendants(parentArn: string) {
      const descendants = []
      const children = parentChildMap.get(parentArn)
      if (children) {
        children.forEach((child: SelectableResource) => {
          descendants.push(child)
          descendants.push(..._getDescendants(child.resourceArn))
        })
      }
      return descendants
    }

    // Add direct dependees and descendents of rule matches.  These are only applied to direct rule matches (not
    // transitively).
    let directDependeeArns: string[] = []
    for (let dependerArn of transitiveMatchesToProcess) {
      // Skip if this resource is excluded
      if (excludedResourceArns.has(dependerArn)) {
        continue
      }

      let depender = appResourceMap.get(dependerArn)
      if (depender) {
        for (let dependeeArn of depender.directDependees) {
          let dependee = appResourceMap.get(dependeeArn)
          if (dependee && !excludedResourceArns.has(dependeeArn)) {
            directDependeeArns.push(dependeeArn)
            dependee.directDependeeOf.add(dependerArn)
          }
        }

        const descendants = _getDescendants(dependerArn)
        descendants.forEach((descendant: SelectableResource) => {
          if (!excludedResourceArns.has(descendant.resourceArn)) {
            directDependeeArns.push(descendant.resourceArn)
          }
        })
      }
    }

    while (transitiveMatchesToProcess.length > 0 || directDependeeArns.length > 0) {
      let dependentResourceArn = null
      if (transitiveMatchesToProcess.length > 0) {
        dependentResourceArn = transitiveMatchesToProcess.pop()
      } else {
        dependentResourceArn = directDependeeArns.pop()
      }

      // Skip if this resource is excluded
      if (excludedResourceArns.has(dependentResourceArn)) {
        continue
      }

      let dependentResource = appResourceMap.get(dependentResourceArn)
      dependentResource.dependees.forEach((dependeeArn: string) => {
        // Skip if the dependee is excluded
        if (excludedResourceArns.has(dependeeArn)) {
          return
        }

        // add this resource to dependeeOf on each dependee resource.
        let dependeeResource = appResourceMap.get(dependeeArn)
        if (dependeeResource) {
          // add the dependentResourceArn to the dependeeOf list on the dependee resource.
          dependeeResource.dependeeOf.add(dependentResourceArn)
          // If we haven't already processed this dependentResource, we need to
          // queue it up for processing.
          if (!processedMatches.has(dependeeArn)) {
            transitiveMatchesToProcess.push(dependeeArn)
          }
        }
      })

      if (dependentResource.weakDependees.length > 0) {
        // Do the same dependee calculation for weak dependees, but only if they aren't being excluded
        dependentResource.weakDependees.forEach((weakDependeeArn: string) => {
          // Skip if the weak dependee is excluded
          if (excludedResourceArns.has(weakDependeeArn)) {
            return
          }

          // Only add to weakDependeeOf if the resource is not excluded
          let weakDependeeResource = appResourceMap.get(weakDependeeArn)
          if (weakDependeeResource) {
            if (!suppressedArns.has(weakDependeeArn)) {
              weakDependeeResource.weakDependeeOf.add(dependentResourceArn)
              // If we haven't already processed this dependentResource, we need to
              // queue it up for processing.
              if (!processedMatches.has(weakDependeeArn)) {
                transitiveMatchesToProcess.push(weakDependeeArn)
              }
            } else {
              weakDependeeResource.suppressedWeakDependeeOf.add(dependentResourceArn)
            }
          }
        })
      }
      // collect this dependentResourceArn in our list of already processed dependent resources
      processedMatches.add(dependentResourceArn)
    }

    // Process exclude rules AFTER all transitive processing is complete
    // Exclude rules take precedence and override any previous selections
    applicationResources.forEach((resource) => {
      if (resource.resourceArn && resource.tags) {
        // Use the extracted helper function for exclude rule matching
        const excludeRuleResults = Application._matchesRuleSet(resource, excludingRules, false)

        // If resource matches exclude rule, clear all selections
        if (excludeRuleResults.size > 0) {
          // Clear any matching tags set by include rules
          resource.matchingRules.clear()
          resource.arnMatch = false
          // Clear all dependency relationships that would cause this resource to be selected
          resource.dependeeOf.clear()
          resource.weakDependeeOf.clear()
          resource.directDependeeOf.clear()
          // Mark as excluded for UI purposes
          resource.isExcluded = true
        }
      }
    })
  }

  static updateApplicationResourceRuleSelection(
    applications: Array<Application>,
    applicationResources: Array<SelectableResource>
  ) {
    /**
     * Process resource rules for a set of Application objects.
     */

    // initialize the selection rule accounting fields.
    applicationResources.forEach((resource) => {
      resource.arnMatch = false
      resource.matchingRules = new Map<string, MatchingRuleInfo>()
      resource.dependeeOf = new Set<string>()
      resource.weakDependeeOf = new Set<string>()
      resource.suppressedWeakDependeeOf = new Set<string>()
      resource.directDependeeOf = new Set<string>()
      resource.isExcluded = false
    })

    for (let application of applications) {
      Application._updateResourceRuleSelection(
        application.matchingArns,
        application.suppressedArns,
        application.matchingRules,
        application.excludingRules,
        applicationResources
      )
    }
  }

  static updateResourceRuleSelection(
    matchingArns: Set<Arn>,
    excludedWeakArns: Set<Arn>,
    matchingRules: Array<Tag | AllRule | ResourceTypeRule | ResourceGroupRule>,
    excludingRules: Array<Tag | AllRule | ResourceTypeRule | ResourceGroupRule>,
    applicationResources: Array<SelectableResource>
  ) {
    /**
     * Apply rules outside of the context of an Application.  This is used in experiences
     * where we are creating a new application and don't yet have an Application object.
     */

    // initialize the selection rule accounting fields.
    applicationResources.forEach((resource) => {
      resource.arnMatch = false
      resource.matchingRules = new Map<string, MatchingRuleInfo>()
      resource.dependeeOf = new Set<string>()
      resource.weakDependeeOf = new Set<string>()
      resource.suppressedWeakDependeeOf = new Set<string>()
      resource.directDependeeOf = new Set<string>()
      resource.isExcluded = false
    })

    Application._updateResourceRuleSelection(
      matchingArns,
      excludedWeakArns,
      matchingRules,
      excludingRules,
      applicationResources
    )
  }

  // pub-sub for application updates as they happen.
  static publishSubject$ = new CrudSubject<Application>()

  // cached timestamp for successful test result
  private _successfulFailoverTestTimestamp: Date = null
  private _successfulFailoverTestQueryServerTimestamp: Date = null

  // cached sync phase override
  private _syncPhaseOverride: SyncPhaseDto = null

  // cached network sandboxing override
  private _networkSandboxOverride: boolean = null
  private _quarantineOverride: boolean = null

  private _resources: ApplicationResourceDto[] = null
  private _backupStatuses: Map<string, AppResourceStatusChangeDto> = new Map()
  private _restoreStatuses: Map<string, AppResourceStatusChangeDto> = new Map()
  private _failbackSatuses: Map<string, AppResourceStatusChangeDto> = new Map()
  private _backupStatusNextToken: string = null
  private _restoreStatusNextToken: string = null
  private _failbackStatusNextToken: string = null

  private _rulesFromServer: BaseRuleDto[] = null

  // used to save the applied RP ID for the app when we last refreshed the list of resources
  // when the RP changes the list of resources we need to display may also change and we need
  // to refresh it
  private _lastResourceRefreshRp: string = null

  // sync source and sync pair
  private _syncSource: SyncSource = null
  private _syncPair: SyncPair = null
  private _syncPairLastQueryServerTimestamp: Date = null

  // Recovery point attempt DTOs (multi-account only)
  private _recoveryPointAttempts: RecoveryPointAttemptDto[] = null
  private _recoveryPointAttemptsServerTimestamp: Date = null
  private _recoveryPoints: Array<RecoveryPointDto> = null
  private _recoveryPointsServerTimestamp: Date = null

  // Application Issues DTOs (multi-account only)
  private _applicationIssueDtos: Array<ApplicationIssueDto> = null
  private _applicationIssueDtosServerTimestamp: Date = null

  // Integrations associted with this application:
  private _integrationDtos: Array<ApplicationIntegrationDto> = []

  private _appResourceCache: Map<RelevantStatus, [Date, Array<ApplicationResource>]> = null

  // A timestamp that's updated by the foreground refresh loop after resources and statuses are updated.  Used for
  // invalidation of entries in the _appResourceCache
  private _refreshTimestamp = new Date()

  // Track previous processing states to detect job completion transitions
  private _wasBackingUp = false
  private _wasRestoring = false
  private _wasFailingBack = false

  constructor(
    private applicationDto: ApplicationDto,
    private apiService: ApiService,
    private accountService: AccountService,
    private syncSourceProviderService: SyncSourceProviderService,
    private syncPairProviderService: SyncPairProviderService
  ) {
    this.applicationDto = applicationDto
    this._appResourceCache = new Map()
  }

  static create(
    applicationDto: ApplicationDto,
    apiService: ApiService,
    accountService: AccountService,
    ssProviderService: SyncSourceProviderService,
    spProviderService: SyncPairProviderService
  ) {
    /*
    Create an application object from a DTO and publish it for subscribers to get notified
    */
    let newApp = new Application(applicationDto, apiService, accountService, ssProviderService, spProviderService)
    Application.publishSubject$.upsert(newApp, newApp.applicationId)
    return newApp
  }

  static createApplication$(
    apiService: ApiService,
    accountService: AccountService,
    ssProviderService: SyncSourceProviderService,
    spProviderService: SyncPairProviderService,
    accountId: string,
    applicationType: ApplicationType,
    applicationName: string,
    applicationRpo: number,
    applicationNotificationEmails: Array<string>,
    sourceEndpointUri: string,
    targetEndpointUri: string,
    selectionRules: Array<Tag | AllRule | ResourceTypeRule | ResourceGroupRule>,
    selectionArns: Set<Arn>,
    excludeWeakArns: Set<Arn>,
    identityProviderId: string = null
  ) {
    /*
      Invoke the server API to create a new application.
    */

    // Build selectionRules array, supporting Tag, AllRule, ResourceTypeRule, and ResourceGroupRule
    let ruleDtos = selectionRules
      .map((rule) => {
        // Check if the tag/rule has an action property indicating it's an exclude rule
        const action = (rule as any).action

        if ('rules' in rule) {
          return createAllRuleDto(rule as AllRule, action)
        } else if (rule instanceof ResourceTypeRule) {
          return createResourceTypeRuleDto(rule, action)
        } else if (rule instanceof ResourceGroupRule) {
          return createResourceGroupRuleDto(rule, action)
        } else {
          return createTagRuleDto(rule as Tag, action)
        }
      })
      .filter((rule) => {
        if (rule.ruleType === 'all') {
          ;(rule as AllRuleDto).rules = Application.prototype.filterEmptyNameRules((rule as AllRuleDto).rules)
          return (rule as AllRuleDto).rules.length > 0
        }
        return true
      })

    let arnRule = null
    if (selectionArns.size > 0) {
      arnRule = createArnRuleDto(selectionArns)
    }
    let excludeWeakArnRule = null
    if (excludeWeakArns.size > 0) {
      excludeWeakArnRule = createExcludeWeakArnRuleDto(excludeWeakArns)
    }

    return apiService
      .createApplication(
        accountId,
        applicationType,
        applicationName,
        applicationRpo,
        applicationNotificationEmails,
        sourceEndpointUri,
        targetEndpointUri,
        ruleDtos,
        arnRule,
        excludeWeakArnRule,
        identityProviderId
      )
      .pipe(
        map((appDto) => Application.create(appDto, apiService, accountService, ssProviderService, spProviderService))
      )
  }

  static loadAllApplications$(
    accountId: string,
    apiService: ApiService,
    accountService: AccountService,
    ssProviderService: SyncSourceProviderService,
    spProviderService: SyncPairProviderService
  ) {
    /*
      Load all applications from the server.  This is invoked on initial load of the client to populate the application cache and notify the navigation pane
      of the applications that should be displayed.
    */
    return apiService.listApplications(accountId).pipe(
      map((appDtos) => {
        appDtos.map((appDto) =>
          Application.create(appDto, apiService, accountService, ssProviderService, spProviderService)
        )
      })
    )
  }

  static loadApplication$(
    accountId: string,
    applicationId: string,
    apiService: ApiService,
    accountService: AccountService,
    ssProviderService: SyncSourceProviderService,
    spProviderService: SyncPairProviderService,
    forceRefreshResources: boolean = false
  ) {
    /*
      Load 1 application from the server.  This is called from the component for an application via the ApplicationProviderService.
    */
    return apiService.getApplication(accountId, applicationId).pipe(
      map((appDto) => Application.create(appDto, apiService, accountService, ssProviderService, spProviderService)),
      switchMap((app) => app.refresh$(false, true, forceRefreshResources))
    )
  }

  static sameSyncPair(app1: Application, app2: Application) {
    return (
      app1.sourceAwsAccountId === app2.sourceAwsAccountId &&
      app1.sourceRegion === app2.sourceRegion &&
      app1.targetAwsAccountId === app2.targetAwsAccountId &&
      app1.targetRegion === app2.targetRegion
    )
  }

  private setSyncPair(syncPair: SyncPair) {
    this._syncPair = syncPair
    this._syncPairLastQueryServerTimestamp = this.maxProcessingCompletionDate()
  }

  initiateBackup$() {
    /*
      Initiates a backup of just this application and poll until it completes.
    */
    return this.apiService.initiateBackup(this.accountId, [this.applicationId]).pipe(
      tap((backupResponseDto) => {
        let thisAppDto = backupResponseDto.apps.find((appDto) => appDto.appId === this.applicationId)
        if (thisAppDto) {
          this.applicationDto = thisAppDto
        }
      }),
      delay(Application.BACKUP_POLL_INTERVAL),
      switchMap((_backupResponseDto) => {
        return this.refresh$(true)
      })
    )
  }

  initiateRestore$(useLatestRecoveryPoint: boolean = false, forceNewTargetPlan: boolean = false) {
    /*
      Initiate a restore of just this application and poll until it completes.
    */
    let restoreRequests = new Map<string, ApplicationRestoreRequestDto>([
      [this.applicationId, {} as ApplicationRestoreRequestDto],
    ])

    if (forceNewTargetPlan) {
      restoreRequests.get(this.applicationId).forceNewTargetPlan = forceNewTargetPlan
    }

    let buildRestoreRequests$ = of(restoreRequests)

    if (useLatestRecoveryPoint) {
      buildRestoreRequests$ = this.apiService.listApplicationRecoveryPoints(this.accountId, this.applicationId).pipe(
        map((recoveryPoints) => {
          const latestRecoveryPoint = recoveryPoints.reduce((latest, current) => {
            return current.timestamp > (latest?.timestamp || 0) ? current : latest
          }, null)

          if (latestRecoveryPoint) {
            restoreRequests.get(this.applicationId).recoveryPointId = latestRecoveryPoint.recoveryPointId
          }
          return restoreRequests
        })
      )
    }

    // Now that we have an observable to create the restore request object, pipe that to the initiateRestore() API
    // and then launch a refresh on this application to monitor its restoration.
    return buildRestoreRequests$.pipe(
      switchMap((restoreRequests) =>
        this.apiService.initiateRestore(this.accountId, { apps: mapToObject(restoreRequests) })
      ),
      tap((restoreResponseDto) => {
        const thisAppDto = restoreResponseDto.apps.find((appDto) => appDto.appId === this.applicationId)
        if (thisAppDto) {
          this.applicationDto = thisAppDto
        }
      }),
      delay(Application.RESTORE_POLL_INTERVAL),
      switchMap((_restoreResponseDto) => {
        return this.refresh$(true)
      })
    )
  }

  initiateRestoreFromJob$() {
    /*
      Initiate a new restore based on the application's last restore Job.
    */
    let restoreRequestDto: RestoreRequestDto = { retryJobId: this.applicationDto.restoreStatus.lastJobId }

    return this.apiService.initiateRestore(this.accountId, restoreRequestDto).pipe(
      tap((restoreResponseDto) => {
        const thisAppDto = restoreResponseDto.apps.find((appDto) => appDto.appId === this.applicationId)
        if (thisAppDto) {
          this.applicationDto = thisAppDto
        }
      }),
      delay(Application.RESTORE_POLL_INTERVAL),
      switchMap((_restoreResponseDto) => {
        return this.refresh$(true)
      })
    )
  }

  /* This method refreshes every 5 seconds for 240 seconds */
  private _startExtendedIssueRefresh() {
    // Periodic refresh of issues for 240 seconds after job completion
    const refreshInterval = 5000 // 5 seconds
    const refreshDuration = 240000 // 240 seconds

    timer(0, refreshInterval)
      .pipe(
        takeUntil(timer(refreshDuration)),
        switchMap(() => this.apiService.listApplicationIssues(this.accountId, this.applicationId)),
        tap((issueDtos) => {
          this.setIssues(issueDtos)
          Application.publishSubject$.upsert(this, this.applicationId)
        })
      )
      .subscribe()
  }

  public backgroundRefresh$(refreshDto: boolean) {
    let pollIntervals = new BehaviorSubject<number>(0)
    return pollIntervals.pipe(
      switchMap((interval) => timer(interval)), // wait for however long the interval from the BehaviorSubject tells us to wait
      switchMap((_) => {
        // build an observable that will refresh the application, including all of the additional
        // fields on the application that come along with multi-account
        let appDto$ = of(this.applicationDto)
        if (refreshDto) {
          appDto$ = this.apiService.getApplication(this.accountId, this.applicationId)
        }

        // after the first pass, we always want to refresh the appDto if it's multi-account.
        refreshDto = true

        return appDto$
      }),
      switchMap((appDto) => {
        this.applicationDto = appDto
        // refresh issues if needed
        let issues$ = of(this._applicationIssueDtos)
        if (
          this.currentlyActive || // currently processing something and therefore could be generating new issues
          maxDate(maxDate(this.lastBackupComplete, this.lastRestoreComplete), this.lastFailbackComplete) >
            this._applicationIssueDtosServerTimestamp // has completed since last query
        ) {
          issues$ = this.apiService.listApplicationIssues(appDto.accountId, appDto.appId)
        }
        return issues$
      }),
      map((issues) => {
        this.setIssues(issues)

        pollIntervals.next(Application.BACKGROUND_POLL_INTERVAL)

        // Re-publish to components that are monitoring applications
        Application.publishSubject$.upsert(this, this.applicationId)

        return this
      })
    )
  }

  private _applyStatusChanges(
    statuses: Map<string, AppResourceStatusChangeDto>,
    changes: AppResourceStatusChangeDto[]
  ) {
    // Apply status changes from the server.  They are returned in order so we apply them in order where the last
    // change for a given arn wins.
    for (const change of changes) {
      if (change.state) {
        statuses.set(change.arn, change)
      } else {
        statuses.delete(change.arn)
      }
    }
  }

  private _buildRefreshObservable$(refreshDto: boolean, forceRefreshResources: boolean = false) {
    // This method implements an rxjs version of the _buildRefreshObservable$ method in the SyncPair
    // and SyncSource classes.  I thought that might make it easier to read, but it's still a bitch.

    // This subject will emit the polling interval values.  We want to poll immediately the first time, so
    // we construct with a zero value.  Subsequent poll intervals are provided to the subject based upon
    // the state of the application and whether it's currently backing up or restoring.
    let pollIntervals = new BehaviorSubject<number>(0)
    let firstPoll = true

    return pollIntervals.pipe(
      switchMap((interval) => timer(interval)), // wait for however long the interval from the BehaviorSubject tells us to wait
      switchMap((_) => {
        // build an observable that will refresh the application, including all of the additional
        // fields on the application that come along with multi-account
        let appDto$ = of(this.applicationDto)
        if (refreshDto) {
          appDto$ = this.apiService.getApplication(this.accountId, this.applicationId)
        }

        // after the first pass, we always want to refresh the appDto
        refreshDto = true

        return appDto$
      }),
      switchMap((appDto) => {
        // build an observable that will kick off parallel updates of the various fields that are maintained
        // on the application object.  None of these observables should poll, so this will complete relatively quickly.
        this.applicationDto = appDto

        const oldRules = this._rulesFromServer
        const newRules = appDto.selectionRules
        const rpChanged = this._lastResourceRefreshRp !== appDto.recoveryPointId
        const rulesChanged = !areDeeplyEqual(oldRules, newRules)

        // Detect job completion transitions - when a job just finished, force a resource refresh
        // to ensure we get the final status for all resources
        const backupJustCompleted = this._wasBackingUp && !this.currentlyBackingUp
        const restoreJustCompleted = this._wasRestoring && !this.currentlyRestoring
        const failbackJustCompleted = this._wasFailingBack && !this.currentlyFailingBack
        const jobJustCompleted = backupJustCompleted || restoreJustCompleted || failbackJustCompleted

        // Update previous processing state tracking
        this._wasBackingUp = this.currentlyBackingUp
        this._wasRestoring = this.currentlyRestoring
        this._wasFailingBack = this.currentlyFailingBack

        this._rulesFromServer = newRules

        // refresh app resources if needed
        let resources$ = of({
          resources: this._resources,
          backupStatusNextToken: this._backupStatusNextToken,
          restoreStatusNextToken: this._restoreStatusNextToken,
          failbackStatusNextToken: this._failbackStatusNextToken,
        } as ApplicationResourcesResponseDto)

        // Refresh the resource list if the app's rules changed, or we don't have a status token for the current job,
        // or a backup recently completed, or a job just completed (to get final embedded statuses).
        if (
          rulesChanged ||
          rpChanged || // if the RP changed we might have different resources to display
          forceRefreshResources ||
          jobJustCompleted || // force refresh when any job just completed to sync final statuses
          (this.currentlyBackingUp && !this._backupStatusNextToken) ||
          (this.currentlyRestoring && !this._restoreStatusNextToken) ||
          (this.currentlyFailingBack && !this._failbackStatusNextToken) ||
          this.lastBackupComplete > this._recoveryPointAttemptsServerTimestamp
        ) {
          this._lastResourceRefreshRp = appDto.recoveryPointId
          resources$ = this.apiService.listApplicationResources(appDto.accountId, appDto.appId)
        }
        resources$ = resources$.pipe(
          tap((response) => {
            this._resources = response.resources
            this._backupStatusNextToken = response.backupStatusNextToken
            this._restoreStatusNextToken = response.restoreStatusNextToken
            this._failbackStatusNextToken = response.failbackStatusNextToken
          })
        )

        // refresh backup statuses if we are currently backing up or recently completed backup
        let backupStatusChanges$ = of({
          changes: [],
          nextToken: this._backupStatusNextToken,
        } as AppResourceStatusChangesResponseDto)

        if ((firstPoll || this.currentlyBackingUp || this.backupRecentlyCompleted) && this._backupStatusNextToken) {
          backupStatusChanges$ = this.apiService.listApplicationResourceStatusChanges(
            appDto.accountId,
            appDto.appId,
            'backup',
            this._backupStatusNextToken
          )
        }
        backupStatusChanges$ = backupStatusChanges$.pipe(
          tap((response) => {
            this._backupStatusNextToken = response.nextToken || this._backupStatusNextToken
            this._applyStatusChanges(this._backupStatuses, response.changes)
          })
        )

        // refresh restore statuses if we are currently restoring up or recently completed restoring
        let restoreStatusChanges$ = of({
          changes: [],
          nextToken: this._restoreStatusNextToken,
        } as AppResourceStatusChangesResponseDto)

        if ((firstPoll || this.currentlyRestoring || this.restoreRecentlyCompleted) && this._restoreStatusNextToken) {
          restoreStatusChanges$ = this.apiService.listApplicationResourceStatusChanges(
            appDto.accountId,
            appDto.appId,
            'restore',
            this._restoreStatusNextToken
          )
        }
        restoreStatusChanges$ = restoreStatusChanges$.pipe(
          tap((response) => {
            this._restoreStatusNextToken = response.nextToken || this._restoreStatusNextToken
            this._applyStatusChanges(this._restoreStatuses, response.changes)
          })
        )

        // refresh failback statuses if we are currently failing back or recently completed failing back
        let failbackStatusChanges$ = of({
          changes: [],
          nextToken: this._failbackStatusNextToken,
        } as AppResourceStatusChangesResponseDto)

        if (
          (firstPoll || this.currentlyFailingBack || this.failbackRecentlyCompleted) &&
          this._failbackStatusNextToken
        ) {
          failbackStatusChanges$ = this.apiService.listApplicationResourceStatusChanges(
            appDto.accountId,
            appDto.appId,
            'failback',
            this._failbackStatusNextToken
          )
        }
        failbackStatusChanges$ = failbackStatusChanges$.pipe(
          tap((response) => {
            this._failbackStatusNextToken = response.nextToken || this._failbackStatusNextToken
            this._applyStatusChanges(this._failbackSatuses, response.changes)
          })
        )

        // refresh the recovery points if needed
        let rps$ = of(this.recoveryPoints)
        if (this.lastBackupSuccess > this._recoveryPointsServerTimestamp) {
          rps$ = this.apiService.listApplicationRecoveryPoints(appDto.accountId, appDto.appId)
        }
        rps$ = rps$.pipe(
          tap((rps) => {
            this.setRecoveryPoints(rps)
          })
        )

        // refresh recovery point attempts if needed
        let rpas$ = of(this.recoveryPointAttempts)
        if (this.lastBackupComplete > this._recoveryPointAttemptsServerTimestamp) {
          const dateFrom = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000) // last 14 days
          rpas$ = this.apiService.listApplicationRecoveryPointAttempts(appDto.accountId, appDto.appId, dateFrom)
        }
        rpas$ = rpas$.pipe(
          tap((rpas) => {
            this.setRecoveryPointAttempts(rpas)
          })
        )

        // refresh issues if needed
        let issues$ = of(this._applicationIssueDtos)
        if (
          this.currentlyActive || // currently processing something and therefore could be generating new issues
          maxDate(maxDate(this.lastBackupComplete, this.lastRestoreComplete), this.lastFailbackComplete) >
            this._applicationIssueDtosServerTimestamp // has completed since last query
        ) {
          issues$ = this.apiService.listApplicationIssues(appDto.accountId, appDto.appId)
        }
        issues$ = issues$.pipe(
          tap((issues) => {
            this.setIssues(issues)
          })
        )

        // refresh lastfailover test timestamp
        let lastSuccessfulFailoverTest$ = of(this._successfulFailoverTestTimestamp)
        if (this.lastRestoreRequest > this._successfulFailoverTestQueryServerTimestamp) {
          lastSuccessfulFailoverTest$ = this.getLatestSuccessfulFailoverTestResultTimestamp$().pipe(
            tap((lsft) => {
              this._successfulFailoverTestQueryServerTimestamp = this.lastRestoreRequest
            })
          )
        }
        lastSuccessfulFailoverTest$ = lastSuccessfulFailoverTest$.pipe(
          tap((lsft) => {
            this._successfulFailoverTestTimestamp = lsft
          })
        )

        // if this is multi-account, we also refresh the sync pair at this time if we're currently processing
        // or if we haven't polled since the most recent processing completed.
        let spRefresh$ = of(this._syncPair)
        if (
          this.lastBackupStart >= this.lastBackupComplete ||
          this.lastRestoreStart >= this.lastRestoreComplete ||
          this.lastFailbackStart >= this.lastFailbackComplete ||
          this.maxProcessingCompletionDate() > this._syncPairLastQueryServerTimestamp
        ) {
          if (this._syncPair) {
            spRefresh$ = this._syncPair.refreshSyncPair$(this.backupStatus, this.restoreStatus, this.failbackStatus) // shouldn't poll when backupStatus and restoreStatus provided
          } else {
            spRefresh$ = this.syncPairProviderService.loadSyncPair$(
              this.accountId,
              this.sourceEndpointUri,
              this.targetEndpointUri,
              this.backupStatus,
              this.restoreStatus,
              this.failbackStatus
            )
          }
        }
        spRefresh$ = spRefresh$.pipe(
          tap((sp) => {
            this.setSyncPair(sp)
          })
        )

        // For multi-account, we also need to load the associated integrations
        let integrationRefresh$ = of(this._integrationDtos)
        if (!this._integrationDtos) {
          integrationRefresh$ = this.apiService.listApplicationIntegrations(this.accountId, this.applicationId)
          integrationRefresh$ = integrationRefresh$.pipe(
            tap((integrationDtos) => {
              this._integrationDtos = integrationDtos
            })
          )
        }

        /* This function checks if a job has been completed
             by verifying that both start and end times are defined
             and that the end time is later than the start time.
          */
        const isJobCompleted = (jobStartTime: Date, jobEndTime: Date) => {
          return jobStartTime && jobEndTime && jobEndTime > jobStartTime
        }

        // Combining the generic currentlyActive flag with specific job status checks (backup, restore, failback) for increased reliability.
        const jobsActive =
          this.currentlyActive || // Fast check if it's correctly initialized and indicates a job is currently active
          this.currentlyBackingUp ||
          this.currentlyRestoring ||
          this.currentlyFailingBack

        const jobsComplete =
          isJobCompleted(this.lastBackupStart, this.lastBackupComplete) &&
          isJobCompleted(this.lastRestoreStart, this.lastRestoreComplete) &&
          isJobCompleted(this.lastFailbackStart, this.lastFailbackComplete)

        // Only start the extended issue refresh if we are sure that no jobs are currently active
        return forkJoin([
          rps$,
          rpas$,
          resources$,
          issues$,
          lastSuccessfulFailoverTest$,
          spRefresh$,
          integrationRefresh$,
          backupStatusChanges$,
          restoreStatusChanges$,
          failbackStatusChanges$,
        ]).pipe(
          tap(() => {
            if (!jobsActive && jobsComplete) {
              this._startExtendedIssueRefresh()
            }
          })
        )
      }),
      map((_) => {
        // All of the internal fields were updated in the prior observable via taps.  All
        // we need to do here is compute the next polling interval and return this object
        // so this becomes a feed of updated Application objects.

        // RESTORE_POLL_INTERVAL is shorter than BACKUP_POLL_INTERVAL so check for that case first.
        if (
          this.currentlyRestoring ||
          this.currentlyFailingBack ||
          this.restoreRecentlyCompleted ||
          this.failbackRecentlyCompleted
        ) {
          pollIntervals.next(Application.RESTORE_POLL_INTERVAL)
        } else if (this.currentlyBackingUp || this.backupRecentlyCompleted) {
          pollIntervals.next(Application.BACKUP_POLL_INTERVAL)
        } else {
          // switch to background poll interval
          pollIntervals.next(Application.BACKGROUND_POLL_INTERVAL)
        }

        firstPoll = false
        this._refreshTimestamp = new Date()

        // Re-publish to components that are monitoring applications
        Application.publishSubject$.upsert(this, this.applicationId)

        return this
      })
    )
  }

  refresh$(refreshDto: boolean, poll: boolean = true, forceRefreshResources: boolean = false) {
    /*
      Load the application from the server, potentially polling until any active processing (backup/restore) completes
    */
    // Query the server for the application details, including the details of the sync source and
    // sync pair object.  This may poll until any in-flight backup, restore, or failback operations
    // have completed.  With each query, timestamps are inspected to see if certain data is possibly
    // updated on the server side, which then initiates additional queries to the server for the
    // possibly updated data (_successfulFailoverTestTimestamp, recovery points, recovery point attempts,
    // and issues).

    let appRefreshObservable = this._buildRefreshObservable$(refreshDto, forceRefreshResources)

    appRefreshObservable = concat(of(this), appRefreshObservable)

    // If we aren't polling, we only want the first 2 emissions from the observable.
    // The first is the current state, the second is the updated state.
    if (!poll) {
      appRefreshObservable = appRefreshObservable.pipe(take(2))
    }

    return appRefreshObservable
  }

  refreshRecoveryPoints$() {
    return this.apiService.listApplicationRecoveryPoints(this.accountId, this.applicationId).pipe(
      switchMap((rpsDto) => {
        this.setRecoveryPoints(rpsDto)
        return of(null)
      })
    )
  }

  private maxProcessingCompletionDate() {
    // We look at the completion dates of backup, restore, and failback to determine the latest point in time that
    // new issues could have been created.  That is then used in the refresh logic to determine if we need to refresh
    // issues again.
    return maxDate(maxDate(this.lastBackupComplete, this.lastRestoreComplete), this.lastFailbackComplete)
  }

  private buildSyntheticDeleteIssue() {
    const scope = this.lastBackupSuccess > this.deletedAt ? RaisedIssueScope.RESTORE : RaisedIssueScope.BACKUP

    const syntheticIssue = new DeleteFailedIssue()
    // Generate a synthetic issue ID for the delete failure
    const syntheticIssueId = `synthetic-delete-failure-${this.applicationId}`
    const raisedIssue = new RaisedIssue(syntheticIssueId, null, scope, syntheticIssue, null)
    return adaptApplicationIssueDto(raisedIssue)
  }

  private setIssues(issues: Array<ApplicationIssueDto>) {
    // Show a synthetic delete issue if:
    // - the application was deleted
    // - there are no other issues
    // - the application is not currently active
    // - there was no successful backup or restore after the delete (prevents showing the issue if the app is fully deleted)
    // - sufficient time has passed since deletion to indicate actual failure
    const hasSyncedAfterDelete =
      this.lastBackupSuccess > this.deletedAt && this.lastRestoreSuccess > this.lastBackupSuccess
    const timeSinceDeletion = this.deletedAt ? Date.now() - this.deletedAt.getTime() : 0
    const deletionTimeoutMs = 30 * 60 * 1000 // 30 minutes

    if (
      issues &&
      issues.length == 0 &&
      this.deleted &&
      !this.currentlyActive &&
      !hasSyncedAfterDelete &&
      timeSinceDeletion > deletionTimeoutMs
    ) {
      issues.push(this.buildSyntheticDeleteIssue())
    }

    this._applicationIssueDtos = issues
    this._applicationIssueDtosServerTimestamp = this.maxProcessingCompletionDate()
  }

  private setRecoveryPointAttempts(rpas: Array<RecoveryPointAttemptDto>) {
    this._recoveryPointAttempts = rpas
    this._recoveryPointAttemptsServerTimestamp = this.lastBackupComplete
  }

  private setRecoveryPoints(rps: Array<RecoveryPointDto>) {
    this._recoveryPoints = rps
    this._recoveryPointsServerTimestamp = this.lastBackupComplete
  }

  filterEmptyNameRules(rules: BaseRuleDto[]): BaseRuleDto[] {
    return rules.filter((rule) => {
      // Filter out rules with empty names
      if (rule.ruleType === 'tag' && (rule as TagRuleDto).name === '') {
        return false
      }
      // Filter out ResourceTypeRule with empty resourceType
      if (rule.ruleType === 'resourceType' && (rule as ResourceTypeRuleDto).resourceType === '') {
        return false
      }
      // Recursively filter AllRuleDto
      if (rule.ruleType === 'all') {
        ;(rule as AllRuleDto).rules = this.filterEmptyNameRules((rule as AllRuleDto).rules)
        return (rule as AllRuleDto).rules.length > 0 // Keep the AllRule only if it has non-empty rules
      }
      return true
    })
  }

  update$(
    updatedName: string,
    updatedRpo: number,
    updatedNotificationEmails: Array<string>,
    updatedSelectionRules: Array<Tag | AllRule | ResourceTypeRule | ResourceGroupRule>,
    updatedExcludingRulesOrAllRule: Array<Tag | AllRule | ResourceTypeRule | ResourceGroupRule>,
    updatedSelectionArns: Set<Arn>,
    updatedExcludeWeakArns: Set<Arn>,
    integrationIds: Set<string>,
    identityProviderId: string = null
  ) {
    // Process the updatedSelectionRules array to create a unified array of DTOs
    let selectionRules: Array<BaseRuleDto> = updatedSelectionRules
      .map((rule) => {
        if ('rules' in rule) {
          // It's an AllRule
          return createAllRuleDto(rule as AllRule)
        } else if (rule instanceof ResourceTypeRule) {
          // It's a ResourceTypeRule
          return createResourceTypeRuleDto(rule)
        } else if (rule instanceof ResourceGroupRule) {
          // It's a ResourceGroupRule
          return createResourceGroupRuleDto(rule)
        } else {
          // It's a Tag
          return createTagRuleDto(rule as Tag)
        }
      })
      .filter((rule) => {
        // Filter out empty AllRules
        if (rule.ruleType === 'all') {
          ;(rule as AllRuleDto).rules = this.filterEmptyNameRules((rule as AllRuleDto).rules)
          return (rule as AllRuleDto).rules.length > 0 // Keep the AllRule only if it has non-empty rules
        }
        return true // Keep everything else
      })

    // Process the updatedExcludingRulesOrAllRule array to create exclude rules
    let excludeRules: Array<BaseRuleDto> = updatedExcludingRulesOrAllRule
      .map((rule) => {
        if ('rules' in rule) {
          // It's an AllRule
          return createAllRuleDto(rule as AllRule, 'exclude')
        } else if (rule instanceof ResourceTypeRule) {
          // It's a ResourceTypeRule
          return createResourceTypeRuleDto(rule, 'exclude')
        } else if (rule instanceof ResourceGroupRule) {
          // It's a ResourceGroupRule
          return createResourceGroupRuleDto(rule, 'exclude')
        } else {
          // It's a Tag
          return createTagRuleDto(rule as Tag, 'exclude')
        }
      })
      .filter((rule) => {
        // Filter out empty AllRules
        if (rule.ruleType === 'all') {
          ;(rule as AllRuleDto).rules = this.filterEmptyNameRules((rule as AllRuleDto).rules)
          return (rule as AllRuleDto).rules.length > 0 // Keep the AllRule only if it has non-empty rules
        }
        return true // Keep everything else
      })

    // Combine include and exclude rules
    selectionRules = [...selectionRules, ...excludeRules]

    // Append Arn and ExcludeWeakArn rules to selectionRules
    const updatedArnRule = createArnRuleDto(updatedSelectionArns)
    if (updatedArnRule.arns.length > 0) {
      selectionRules.push(updatedArnRule as BaseRuleDto)
    }

    const updatedExcludeWeakArnRule = createExcludeWeakArnRuleDto(updatedExcludeWeakArns)
    if (updatedExcludeWeakArnRule.arns.length > 0) {
      selectionRules.push(updatedExcludeWeakArnRule as BaseRuleDto)
    }

    let integrationsToAdd = diffSets(integrationIds, this.integrationIds)
    let integrationsToDel = diffSets(this.integrationIds, integrationIds)
    let updates = [] as Array<Observable<null | ApplicationIntegrationDto | ApplicationDto>>

    // Pass the unified selectionRules array to the updateApplication() method
    updates.push(
      this.apiService.updateApplication(
        this.applicationDto,
        updatedName,
        updatedRpo,
        updatedNotificationEmails,
        selectionRules,
        identityProviderId
      )
    )

    integrationsToAdd.forEach((intId) => {
      updates.push(this.apiService.createApplicationIntegration(this.accountId, this.applicationId, intId))
    })

    integrationsToDel.forEach((intId) => {
      updates.push(this.apiService.deleteApplicationIntegration(this.accountId, this.applicationId, intId))
    })
    return forkJoin(updates).pipe(
      switchMap((responses) => {
        let appDto = responses[0] as ApplicationDto
        this.applicationDto = appDto
        return this.apiService.listApplicationIntegrations(this.accountId, this.applicationId)
      }),
      map((integrationDtos) => {
        this._integrationDtos = integrationDtos
        return this
      })
    )
  }

  public loadSourceResources$() {
    if (this._syncSource) {
      return of(this._syncSource.resources)
    } else {
      return this.syncSourceProviderService.loadSyncSource$(this.accountId, this.sourceEndpointUri).pipe(
        map((ss) => {
          this._syncSource = ss
          return ss.resources
        })
      )
    }
  }

  public rescanSourceResources$(additionalIntegrationIds: Set<string>) {
    if (this._syncSource) {
      return this._syncSource.rescanSourceResources$(additionalIntegrationIds, this.identityProviderId).pipe(
        map((ss) => {
          this._syncSource = ss
          return this
        })
      )
    } else {
      return this.syncSourceProviderService.loadSyncSource$(this.accountId, this.sourceEndpointUri).pipe(
        map((ss) => {
          this._syncSource = ss
          return this
        })
      )
    }
  }

  static updateApplicationSyncPhases$(
    primaryApplication: Application,
    restoreRequests: Array<RestoreRequest>,
    syncPhase: SyncPhaseDto
  ) {
    // If we're running a test, override the networkSandboxEnabled flag
    if (syncPhase == SyncPhaseDto.FAILOVER_TEST) {
      restoreRequests.forEach((req) => (req.application._networkSandboxOverride = !!req.networkSandboxEnabled))
    }

    // For multi-account, we need to launch restores of already recovered applications that may not
    // be included in the restoreRequests.  This causes the server to build an expanded restore
    // graph that includes those applications, enabling cross-account relationships to be
    // established.

    // To accommodate this, we'll query for the very latest application DTOs, and include any
    // that aren't currently in standby (and aren't included in this restore request) in
    // the list going to the server.

    // First, built the restore requests that are being asked for here.
    const appRestoreDtos = new Map(restoreRequests.map((req) => adaptRestoreRequest(req, syncPhase)))

    // Now, query for all application DTOs and include the non-standby applications in the restore request
    return primaryApplication.apiService.listApplications(primaryApplication.accountId).pipe(
      switchMap((apps) => {
        apps.forEach((app) => {
          if (
            app.sourceRegion === primaryApplication.applicationDto.sourceRegion &&
            app.targetRegion === primaryApplication.applicationDto.targetRegion
          ) {
            if (!appRestoreDtos.has(app.appId)) {
              if (app.syncPhase != SyncPhaseDto.STANDBY) {
                appRestoreDtos.set(app.appId, {} as ApplicationRestoreRequestDto)
              }
            }
          }
        })

        const restoreRequestDto: RestoreRequestDto = { apps: mapToObject(appRestoreDtos) }
        return primaryApplication.apiService.initiateRestore(primaryApplication.accountId, restoreRequestDto)
      }),
      tap((restoreResponseDto) => {
        restoreResponseDto.apps.forEach((appDto) => {
          // Find the application for this DTO and update its DTO, then publish it.
          let appRequest = restoreRequests.find((req) => req.application.applicationId === appDto.appId)
          if (appRequest) {
            appRequest.application._syncPhaseOverride = syncPhase
            appRequest.application.applicationDto = appDto
            Application.publishSubject$.upsert(appRequest.application, appRequest.application.applicationId)
          }
        })
      }),
      switchMap((_) => of(primaryApplication))
    )
  }

  static initiateFailback$(primaryApplication: Application, allApplications: Array<Application>) {
    return primaryApplication.apiService
      .initiateMultiAccountFailback(
        primaryApplication.accountId,
        allApplications.map((app) => app.applicationId)
      )
      .pipe(switchMap((_failbackResponse) => primaryApplication.refresh$(true)))
  }

  submitFailoverTestResults$(success: boolean, notes: string) {
    let now = new Date()
    if (success) {
      this._successfulFailoverTestTimestamp = now
    }

    return this.apiService
      .submitFailoverTestResults(
        this.accountId,
        this.applicationId,
        this.sourceEndpointUri,
        this.sourceAwsAccountId,
        this.sourceRegion,
        this.targetEndpointUri,
        this.targetAwsAccountId,
        this.targetRegion,
        now,
        success,
        notes
      )
      .pipe(map((_) => this))
  }

  getLatestSuccessfulFailoverTestResultTimestamp$() {
    return this.apiService.listFailoverTestResults(this.accountId, this.applicationId, null, null, true, 1, true).pipe(
      map((testResults) => {
        if (testResults.length > 0) {
          return testResults[0].timestamp
        }
        return null
      })
    )
  }

  get isLoaded(): boolean {
    return !!this._resources
  }

  get latestSuccessfulFailoverTestResultTimestamp() {
    return this._successfulFailoverTestTimestamp
  }

  get resources() {
    return this._resources
  }

  get sourceResources() {
    return this._syncSource && this._syncSource.resources
  }

  buildResources(relevantStatus: RelevantStatus): Array<ApplicationResource> {
    const resources = new Array<ApplicationResource>()
    const appResources = this.resources
    const targetResources = this._syncPair ? this._syncPair.targetResourceMap() : new Map<Arn, AwsTargetResourceDto>()
    const issues = this.issuesMap

    let currentlyProcessing = false
    let processingHorizon = null
    let statuses = new Map()

    if (relevantStatus == RelevantStatus.STAGING) {
      currentlyProcessing = this.currentlyBackingUp
      processingHorizon = this.backupHorizon
      statuses = this._backupStatuses
    } else if (relevantStatus == RelevantStatus.SYNC) {
      currentlyProcessing = this.currentlyRestoring
      processingHorizon = this.restoreHorizon
      statuses = this._restoreStatuses
    } else if (relevantStatus == RelevantStatus.FAILBACK) {
      currentlyProcessing = this.currentlyFailingBack
      processingHorizon = this.failbackHorizon
      statuses = this._failbackSatuses
    } else {
      throw Error(`Unsupported relevantStatus: ${relevantStatus}`)
    }

    for (const res of appResources) {
      const sourceArn = res.arn
      const targetResource = targetResources.get(sourceArn)
      const latestStatus = statuses.get(sourceArn)
      const resourceIssues = issues.get(sourceArn) || new Set<RaisedIssue>()

      const appResource = new ApplicationResource(
        relevantStatus,
        res,
        targetResource,
        latestStatus,
        resourceIssues,
        currentlyProcessing,
        processingHorizon,
        this.type
      )
      resources.push(appResource)
    }

    return resources
  }

  updateResourceStatus(relevantStatus: RelevantStatus, status: ResourceStatus, arns: Set<Arn>) {
    if (this._appResourceCache.has(relevantStatus)) {
      this._appResourceCache.get(relevantStatus)[1].forEach((res) => {
        if (arns.has(res.resourceArn)) {
          res.status = status
        }
      })
    }
  }

  getResources(relevantStatus: RelevantStatus, filterText: string): ApplicationResource[] {
    /**
     * Build a view of the resources to which rules can be applied and that can be rendered
     * in the UI.
     */
    if (!this._resources) {
      return []
    }

    const refreshTimestamp = this._refreshTimestamp
    if (
      !this._appResourceCache.has(relevantStatus) ||
      this._appResourceCache.get(relevantStatus)[0] != refreshTimestamp
    ) {
      this._appResourceCache.set(relevantStatus, [refreshTimestamp, this.buildResources(relevantStatus)])
    }
    let resources = this._appResourceCache.get(relevantStatus)[1]
    resources = resources.filter((appRes) => {
      return (
        appRes.resourceTypeDetails.displayInStatus &&
        (filterText.length === 0 ||
          appRes.displayResourceType.toLowerCase().search(filterText.toLowerCase()) >= 0 ||
          appRes.resourceId.toLowerCase().search(filterText.toLowerCase()) >= 0 ||
          appRes.sourceResourceName.toLowerCase().search(filterText.toLowerCase()) >= 0 ||
          appRes.targetResourceName.toLowerCase().search(filterText.toLowerCase()) >= 0)
      )
    })

    return resources
  }

  /*
    Mark the application as deleted instead of removing it:
    applicationDto.deleted = true; and updates the publishSubject$ cache
    This will keep it in the UI but give it deleted funtionality
  */
  delete$() {
    let applicationId = this.applicationId
    return this.apiService.deleteApplication(this.accountId, applicationId).pipe(
      tap(() => {
        this.setAppDeleted(true)
        Application.publishSubject$.upsert(this, applicationId)
      })
    )
  }

  get deleted(): boolean {
    return this.applicationDto.deleted
  }

  get restoreTrigger(): RestoreTriggerDto | null {
    return this.applicationDto?.restoreTrigger
  }

  private setAppDeleted(value: boolean) {
    this.applicationDto.deleted = value
  }
  get backupStatus() {
    return this.applicationDto.backupStatus
  }

  get restoreStatus() {
    return this.applicationDto.restoreStatus
  }

  get failbackStatus() {
    return this.applicationDto.failbackStatus
  }

  get applicationId() {
    return this.applicationDto.appId
  }

  get accountId() {
    return this.applicationDto.accountId
  }

  get name() {
    return this.applicationDto.name
  }

  get type() {
    return this.applicationDto.type
  }

  get sourceEndpointUri() {
    return this.applicationDto.sourceEndpointUri
  }

  get targetEndpointUri() {
    return this.applicationDto.targetEndpointUri
  }

  // Derives Azure source subscription display name from application resources (_resources),
  // which are loaded on the first refresh poll — no extra API call needed.
  // The subscription resource (Microsoft.Resources/subscriptions) is included in the
  // listApplicationResources response with source.displayName populated.
  get sourceSubscriptionName(): string | null {
    if (this.applicationDto.type !== ApplicationType.AZURE || !this._resources) return null
    const [, sourceSubId] = this.sourceEndpointUri.split(':')
    // source.id is a full Azure path (e.g. "/subscriptions/uuid") — match on the
    // full /subscriptions/ prefix + ID to avoid false positives from embedded GUIDs.
    const sub = this._resources.find(
      (r) => r.type === ResourceType.azureSubscription && r.source?.id?.endsWith(`/subscriptions/${sourceSubId}`)
    )
    return sub?.source?.displayName || null
  }

  // Derives Azure target subscription display name from _syncPair target resources.
  // Unlike source, target data isn't available in _resources (the listApplicationResources
  // call uses includeTargetResources=false for performance). _syncPair loads when the
  // resources tab is visited or during active processing, so the target name appears then.
  get targetSubscriptionName(): string | null {
    if (this.applicationDto.type !== ApplicationType.AZURE || !this._syncPair) return null
    const [, targetSubId] = this.targetEndpointUri.split(':')
    // Resource id is a full Azure path (e.g. "/subscriptions/uuid") — match on the
    // full /subscriptions/ prefix + ID to avoid false positives from embedded GUIDs.
    const sub = this._syncPair.targetResources.find(
      (r) => r.type === ResourceType.azureSubscription && r.id.endsWith(`/subscriptions/${targetSubId}`)
    )
    return sub?.displayName || null
  }

  get sourceAwsAccountId() {
    return this.applicationDto.sourceAwsAccountId
  }

  get sourceRegion() {
    return this.applicationDto.sourceRegion
  }

  get targetAwsAccountId() {
    return this.applicationDto.targetAwsAccountId
  }

  get targetRegion() {
    return this.applicationDto.targetRegion
  }

  get identityProviderId() {
    return this.applicationDto.identityProviderId
  }

  get selectionRules() {
    return this.applicationDto.selectionRules.map((dto: BaseRuleDto) => {
      const rtd = ruleDtoToRule(dto)
      return rtd
    })
  }

  get matchingArns() {
    let matchingArns = new Set<Arn>()
    for (let rule of this.selectionRules) {
      if (rule instanceof ArnRule) {
        rule.arnSet.forEach((arn) => matchingArns.add(arn))
      }
    }
    return matchingArns
  }

  get suppressedArns() {
    let suppressedArns = new Set<Arn>()
    for (let rule of this.selectionRules) {
      if (rule instanceof ExcludeWeakArnRule) {
        rule.arnSet.forEach((arn) => suppressedArns.add(arn))
      }
    }
    return suppressedArns
  }

  /** This code handles what display in the UI */
  get matchingRules() {
    let matchingRules = new Array<Tag | AllRule | ResourceTypeRule | ResourceGroupRule>()
    for (let i = 0; i < this.selectionRules.length; i++) {
      const rule = this.selectionRules[i]
      const ruleDto = this.applicationDto.selectionRules[i]

      // Only include rules without action='exclude' (i.e., include rules)
      if (ruleDto && (ruleDto as any).action === 'exclude') {
        continue // Skip exclude rules
      }

      // Special handling for AllRule - check if it has exclude inner rules
      if (ruleDto && ruleDto.ruleType === 'all' && rule instanceof AllRule) {
        const allRuleDto = ruleDto as AllRuleDto
        const hasExcludeInnerRules = allRuleDto.rules.some((innerRule) => (innerRule as any).action === 'exclude')

        if (hasExcludeInnerRules) {
          // If this AllRule has exclude inner rules, only include the non-exclude rules
          const nonExcludeRules = allRuleDto.rules.filter((innerRule) => (innerRule as any).action !== 'exclude')
          if (nonExcludeRules.length > 0) {
            // Create a new AllRule with only the include rules
            const filteredRule = new AllRule({
              ruleType: 'all',
              rules: nonExcludeRules,
            })
            matchingRules.push(filteredRule as any)
          }
          continue // Skip processing this rule further
        }
        // If no exclude inner rules, fall through to normal processing
      }

      if (rule instanceof TagRule) {
        matchingRules.push({ tagName: rule.tagName, tagValue: rule.tagValue || null, operator: rule.operator } as any)
      } else if (rule instanceof AllRule) {
        matchingRules.push(rule)
      } else if (rule instanceof ResourceTypeRule) {
        matchingRules.push(rule)
      } else if (rule instanceof ResourceGroupRule) {
        matchingRules.push(rule)
      }
    }
    return matchingRules
  }

  get excludingRules() {
    let excludingRules = new Array<Tag | AllRule | ResourceTypeRule | ResourceGroupRule>()
    for (let i = 0; i < this.selectionRules.length; i++) {
      const rule = this.selectionRules[i]
      const ruleDto = this.applicationDto.selectionRules[i]

      // Only include rules with action='exclude'
      if (!ruleDto || (ruleDto as any).action !== 'exclude') {
        // For AllRule, check if any inner rules have action='exclude'
        if (ruleDto && ruleDto.ruleType === 'all' && rule instanceof AllRule) {
          const allRuleDto = ruleDto as AllRuleDto
          const hasExcludeInnerRules = allRuleDto.rules.some((innerRule) => (innerRule as any).action === 'exclude')
          if (hasExcludeInnerRules) {
            // Filter the AllRule to only include exclude rules
            const filteredRule = new AllRule({
              ruleType: 'all',
              rules: allRuleDto.rules.filter((innerRule) => (innerRule as any).action === 'exclude'),
            })
            excludingRules.push(filteredRule as any)
            continue
          }
        }
        continue // Skip non-exclude rules
      }

      if (rule instanceof TagRule) {
        excludingRules.push({ tagName: rule.tagName, tagValue: rule.tagValue || null, operator: rule.operator } as any)
      } else if (rule instanceof AllRule) {
        excludingRules.push(rule)
      } else if (rule instanceof ResourceTypeRule) {
        excludingRules.push(rule)
      } else if (rule instanceof ResourceGroupRule) {
        excludingRules.push(rule)
      }
    }
    return excludingRules
  }

  get rpo() {
    return this.applicationDto.rpo
  }

  get notificationEmails() {
    return this.applicationDto.notificationEmails
  }

  get integrationIds() {
    return new Set<string>(this._integrationDtos.map((dto) => dto.integrationId))
  }

  get syncPhase() {
    return this._syncPhaseOverride || this.applicationDto.syncPhase
  }

  get dto() {
    return { ...this.applicationDto }
  }

  get createdAt() {
    return this.applicationDto.createdAt
  }

  get networkSandboxEnabled(): boolean {
    return this._networkSandboxOverride === null
      ? this.applicationDto.failoverTestSyncConfig.enableNetworkSandbox
      : this._networkSandboxOverride
  }

  get networkSandboxWhitelist(): string[] {
    return this.applicationDto?.failoverTestSyncConfig?.networkSandboxWhitelist || null
  }

  get networkSandboxAllowedDomains(): string[] {
    return this.applicationDto?.failoverTestSyncConfig?.networkSandboxAllowedDomains || []
  }

  get networkSandboxAllowedServices(): string[] {
    return this.applicationDto?.failoverTestSyncConfig?.networkSandboxAllowedServices || []
  }

  get advancedNetworkSandboxEnabled(): boolean {
    return this.applicationDto?.failoverTestSyncConfig?.advancedNetworkSandbox || this.type == ApplicationType.AZURE
  }

  get ransomwareRecoveryEnabled() {
    if (this._quarantineOverride === null) {
      return this.applicationDto.restoreConfig && this.applicationDto.restoreConfig.enableQuarantine
    } else {
      return this._quarantineOverride
    }
  }

  get recoveryPoints() {
    return this._recoveryPoints
  }

  get currentRecoveryPointId() {
    return this.applicationDto.recoveryPointId
  }

  get currentRecoveryPoint(): RecoveryPointDto {
    return (
      this.recoveryPoints &&
      this.recoveryPoints.reduce(
        (accum, item) => (item.recoveryPointId === this.currentRecoveryPointId ? item : accum),
        null
      )
    )
  }

  get recoveryPointAttempts() {
    return this._recoveryPointAttempts
  }

  ////////////////////////////////////////////////////////////////////////////////
  // Processing Timestamps
  ////////////////////////////////////////////////////////////////////////////////
  /////////////////////////////////// BACKUP TIMESTAMPS /////////////////////////////////////

  get currentlyBackingUp() {
    return this.backupStatus && maxDate(this.lastBackupRequest, this.lastBackupStart) > this.lastBackupComplete
  }

  get lastBackupRequest() {
    return this.applicationDto.backupStatus?.lastRequest
  }

  get lastBackupStart() {
    return this.applicationDto.backupStatus?.lastStart
  }

  get lastBackupSuccess() {
    return this.applicationDto.backupStatus?.lastSuccess
  }

  get lastBackupFailure() {
    return this.applicationDto.backupStatus?.lastFailure
  }

  get lastBackupComplete() {
    return maxDate(this.backupStatus?.lastFailure, this.backupStatus?.lastSuccess)
  }

  get backupRecentlyCompleted() {
    return this.backupStatus && this.lastBackupComplete && dateDiff(new Date(), this.lastBackupComplete) < 30 * 1000
  }

  get deletedAt() {
    return this.applicationDto.deletedAt
  }

  private processingHorizon(processingRequest: Date, processingStart: Date, processingComplete): Date {
    // This function is used to determine which resource status should be filtered out when
    // we get resource status from the server.
    if (isLaterThan(processingStart, processingComplete)) {
      // If processing is in progress, use start time.
      return processingStart
    } else if (isLaterThan(processingRequest, processingComplete)) {
      // If a request has happened since processing completed, use request time.
      return processingRequest
    }
    // Default to the time the app was created.
    return this.createdAt
  }

  get backupHorizon(): Date {
    return this.processingHorizon(this.lastBackupRequest, this.lastBackupStart, this.lastBackupComplete)
  }

  get restoreHorizon(): Date {
    return this.processingHorizon(this.lastRestoreRequest, this.lastRestoreStart, this.lastRestoreComplete)
  }

  get failbackHorizon(): Date {
    return this.processingHorizon(this.lastFailbackRequest, this.lastFailbackStart, this.lastFailbackComplete)
  }

  /////////////////////////////////// RESTORE TIMESTAMPS /////////////////////////////////////

  get currentlyRestoring() {
    if (this.restoreStatus) {
      const status = computeProcessStatus(
        this.lastRestoreRequest,
        this.lastRestoreStart,
        this.lastRestoreSuccess,
        this.lastRestoreFailure
      )
      return (
        status === ProcessStatus.FIRST_ATTEMPT_IN_PROGRESS || status === ProcessStatus.SUBSEQUENT_ATTEMPT_IN_PROGRESS
      )
    }

    return false
  }

  get restoreRecentlyCompleted() {
    return this.restoreStatus && this.lastRestoreComplete && dateDiff(new Date(), this.lastRestoreComplete) < 30 * 1000
  }

  get lastRestoreRequest() {
    return this.applicationDto.restoreStatus?.lastRequest
  }

  get lastRestoreStart() {
    return this.applicationDto.restoreStatus?.lastStart
  }

  get lastRestoreSuccess() {
    return this.applicationDto.restoreStatus?.lastSuccess
  }

  get lastRestoreFailure() {
    return this.applicationDto.restoreStatus?.lastFailure
  }

  get lastRestoreComplete(): Date {
    return maxDate(this.lastRestoreFailure, this.lastRestoreSuccess)
  }

  /////////////////////////////////// FAILBACK TIMESTAMPS /////////////////////////////////////

  get currentlyFailingBack() {
    return this.failbackStatus && maxDate(this.lastFailbackRequest, this.lastFailbackStart) > this.lastFailbackComplete
  }

  get lastFailbackRequest() {
    return this.applicationDto.failbackStatus?.lastRequest
  }

  get lastFailbackStart() {
    return this.applicationDto.failbackStatus?.lastRequest
  }

  get lastFailbackSuccess() {
    return this.applicationDto.failbackStatus?.lastSuccess
  }

  get lastFailbackFailure() {
    return this.applicationDto.failbackStatus?.lastFailure
  }

  get lastFailbackComplete() {
    return maxDate(this.lastFailbackSuccess, this.lastFailbackFailure)
  }

  get lastFailback() {
    return this.applicationDto.failbackStatus
  }

  get failbackRecentlyCompleted() {
    return (
      this.failbackStatus && this.lastFailbackComplete && dateDiff(new Date(), this.lastFailbackComplete) < 30 * 1000
    )
  }

  get currentlyActive() {
    return this.currentlyBackingUp || this.currentlyRestoring || this.currentlyFailingBack
  }

  ////////////////////////////////////////////////////////////////////////////////
  // Issue Accessors
  ////////////////////////////////////////////////////////////////////////////////

  private issuesByScope(scopes: ApplicationIssueScopeDto[]): RaisedIssue[] {
    if (this._applicationIssueDtos) {
      return this._applicationIssueDtos
        .filter((issueDto) => scopes.includes(issueDto.scope))
        .map((issueDto) => adaptApplicationIssueDtoToRaisedIssue(issueDto, this))
    } else {
      return []
    }
  }

  get scanIssues() {
    return this.issuesByScope([
      ApplicationIssueScopeDto.SCAN,
      ApplicationIssueScopeDto.REFRESH,
      ApplicationIssueScopeDto.ENHANCE_FOR_RECOVERY_POINT,
    ])
  }

  get backupIssues() {
    return this.issuesByScope([ApplicationIssueScopeDto.BACKUP])
  }

  get restoreIssues() {
    return this.issuesByScope([ApplicationIssueScopeDto.RESTORE])
  }

  get failbackIssues() {
    return this.issuesByScope([ApplicationIssueScopeDto.FAILBACK])
  }

  get allIssues() {
    // Return all types except CLEANUP, which we never show
    return this.issuesByScope([
      ApplicationIssueScopeDto.BACKUP,
      ApplicationIssueScopeDto.RESTORE,
      ApplicationIssueScopeDto.FAILBACK,
      ApplicationIssueScopeDto.REFRESH,
      ApplicationIssueScopeDto.SCAN,
      ApplicationIssueScopeDto.ENHANCE_FOR_RECOVERY_POINT,
    ])
  }

  get recoveryPointIssues() {
    return this.issuesByScope([
      ApplicationIssueScopeDto.SCAN,
      ApplicationIssueScopeDto.REFRESH,
      ApplicationIssueScopeDto.ENHANCE_FOR_RECOVERY_POINT,
      ApplicationIssueScopeDto.BACKUP,
    ])
  }

  get issuesMap() {
    let issuesMap = new Map<Arn, Set<RaisedIssue>>()
    this.allIssues.forEach((issue) => {
      if (!issuesMap.has(issue.arn)) {
        issuesMap.set(issue.arn, new Set<RaisedIssue>())
      }
      issuesMap.get(issue.arn).add(issue)
    })
    return issuesMap
  }

  deleteApplicationIssue$(issueId: string, scope: ApplicationIssueScopeDto) {
    return this.apiService.deleteApplicationIssue(this.accountId, this.applicationId, scope.toString(), issueId)
  }

  setSyncPairResourceConfig$(arn: Arn, config: ResourceConfigDto) {
    return this._syncPair.setSyncPairResourceConfig$(arn, config)
  }

  getSyncPairResourceConfig$(arn: Arn) {
    return this._syncPair.getSyncPairResourceConfig$(arn)
  }
  get manualResourceTemplateUrl$() {
    return this._syncPair.manualResourceTemplateUrl$
  }

  /** Important to feed syncPair data down the component tree */
  get syncPair(): SyncPair {
    return this._syncPair
  }
}

@Injectable({
  providedIn: 'root',
})
export class ApplicationProviderService {
  // cache the latest version of each application
  private applicationCache: Map<string, Application> = null
  private accountId: string = null
  private _loadingAllApplications = false

  constructor(
    private apiService: ApiService,
    private ssProviderService: SyncSourceProviderService,
    private spProviderService: SyncPairProviderService,
    private accountService: AccountService
  ) {
    // subscribe to publications so that we can maintain the cache
    Application.publishSubject$.subscribe((crudEvent) => {
      if (
        crudEvent.action == CrudAction.UPSERT &&
        crudEvent.entity.accountId == this.accountId &&
        !!this.applicationCache
      ) {
        this.applicationCache.set(crudEvent.entityId, crudEvent.entity)
      } else if (!!this.applicationCache) {
        this.applicationCache.delete(crudEvent.entityId)
      }
    })
  }

  loadedApplications() {
    if (!this.applicationCache) {
      return []
    }
    return Array.from(this.applicationCache.values())
  }

  /**
   * Returns the set of identity provider IDs that are used by applications.
   * These are typically MS Entra IdPs used for Azure environment access,
   * and should be excluded from SSO user management IdP dropdowns.
   */
  getUsedIdentityProviderIds(): Set<string> {
    const idpIds = new Set<string>()
    for (const app of this.loadedApplications()) {
      if (app.identityProviderId) {
        idpIds.add(app.identityProviderId)
      }
    }
    return idpIds
  }

  subscribeToApplications$(accountId: string) {
    if (!!this.accountId && accountId != this.accountId) {
      throw Error('Subscribing to applications on the wrong account ID.')
    }

    let publishSubject$ = Application.publishSubject$ as Observable<CrudEvent<Application, string>>
    if (!!this.applicationCache) {
      // If we have cached applications, publish them immediately
      publishSubject$ = concat(
        from(
          Array.from(this.applicationCache.values()).map((application) => {
            return {
              action: CrudAction.UPSERT,
              entity: application,
              entityId: application.applicationId,
            } as CrudEvent<Application>
          })
        ),
        publishSubject$
      )
    }

    return publishSubject$
  }
  loadAllApplications$(accountId: string) {
    /**
     * Query the server for all applications.  This bypasses the cache as it's used to trigger
     * populating the cache.
     */
    this.applicationCache = new Map<string, Application>()
    this.accountId = accountId

    // We need to track the internal state of whether this observable is currently active.
    // To do that, we set a flag upon subscription, but building the actual observable inside
    // of a switchMap which will execute immediately upon subscribing to this observable.
    return of(null).pipe(
      switchMap((_null) => {
        this._loadingAllApplications = true
        return Application.loadAllApplications$(
          accountId,
          this.apiService,
          this.accountService,
          this.ssProviderService,
          this.spProviderService
        )
      }),
      finalize(() => (this._loadingAllApplications = false))
    )
  }

  startBackgroundRefreshForAllApps$() {
    return of(this.loadedApplications()).pipe(
      switchMap((apps) => {
        let refreshers = apps.map((app) => app.backgroundRefresh$(false))
        return merge(...refreshers)
      })
    )
  }

  get loadingAllApplications() {
    return this._loadingAllApplications
  }

  loadApplication$(
    applicationId: string,
    accountId: string,
    refreshDto: boolean = true,
    forceRefreshResources: boolean = false
  ) {
    if (!!this.applicationCache && this.applicationCache.has(applicationId)) {
      let application = this.applicationCache.get(applicationId)
      if (application.accountId != accountId) {
        throw 'The application cache contains applications from the wrong account ID.'
      }
      if (application.isLoaded) {
        return concat(of(application), application.refresh$(refreshDto, true, forceRefreshResources))
      }
      return application.refresh$(refreshDto, true, forceRefreshResources)
    }
    return Application.loadApplication$(
      accountId,
      applicationId,
      this.apiService,
      this.accountService,
      this.ssProviderService,
      this.spProviderService,
      forceRefreshResources
    )
  }

  createApplication$(
    accountId: string,
    applicationType: ApplicationType,
    applicationName: string,
    applicationRpo: number,
    applicationNotificationEmails: Array<string>,
    sourceEndpointUri: string,
    targetEndpointUri: string,
    selectionTags: Array<Tag | AllRule | ResourceTypeRule | ResourceGroupRule>,
    excludingRules: Array<Tag | AllRule | ResourceTypeRule | ResourceGroupRule>,
    selectionArns: Set<Arn>,
    excludeWeakArns: Set<Arn>,
    identityProviderId: string = null
  ) {
    // Mark exclusion tags with action='exclude'
    const markedExclusionTags = excludingRules.map((tag) => {
      if (tag instanceof ResourceTypeRule || tag instanceof ResourceGroupRule || tag instanceof AllRule) {
        // These objects should already handle the action in their internal structure
        return tag
      } else {
        // For regular tags, add the action property
        return { ...tag, action: 'exclude' } as Tag
      }
    })

    const combinedTags = [...selectionTags, ...markedExclusionTags]

    return Application.createApplication$(
      this.apiService,
      this.accountService,
      this.ssProviderService,
      this.spProviderService,
      accountId,
      applicationType,
      applicationName,
      applicationRpo,
      applicationNotificationEmails,
      sourceEndpointUri,
      targetEndpointUri,
      combinedTags,
      selectionArns,
      excludeWeakArns,
      identityProviderId
    )
  }
}
